import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CustomTextInput } from '../../../components/common/inputFields/customTextInput';
import { CustomButton } from '../../../components/common/customButton';
import { useErrorDialog } from '../../../hooks/useErrorDialog';
import { ErrorDialog } from '../../../components/common/errorDialog';
import { getLlmModels, saveLlmModel, type LlmModel } from '../../../services/llmModelsApi';
import '../../../assets/css/Text-General.css';

interface EditableRow extends Omit<LlmModel, 'id'> {
  id?: number;
  isNew?: boolean;
}

export const LLMModels = () => {
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | 'new' | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    setAccessDenied(false);
    const response = await getLlmModels();
    if (response.status === 403) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    if (response.success && response.data) {
      setRows(response.data);
    } else {
      showErrorFromResponse(response);
    }
    setLoading(false);
  };

  const handleChange = (index: number, field: 'provider' | 'model_name', value: string) => {
    setRows((prev) =>
      prev.map((row, idx) =>
        idx === index
          ? {
              ...row,
              [field]: field === 'provider' ? value.toLowerCase() : value,
            }
          : row
      )
    );
  };

  const handleSave = async (index: number) => {
    const row = rows[index];
    if (!row.provider.trim() || !row.model_name.trim()) {
      showErrorFromResponse({
        success: false,
        status: 400,
        message: 'Provider and model name are required',
      });
      return;
    }

    const provider = row.provider.toLowerCase();
    setSavingId(row.id ?? 'new');
    const response = await saveLlmModel({ provider, model_name: row.model_name });
    if (response.status === 403) {
      setAccessDenied(true);
      setSavingId(null);
      return;
    }
    if (response.success && response.data) {
      await fetchModels();
    } else {
      showErrorFromResponse(response);
    }
    setSavingId(null);
  };

  const handleAdd = () => {
    setRows((prev) => [
      ...prev,
      {
        id: undefined,
        isNew: true,
        provider: '',
        model_name: '',
        created_at: '',
        updated_at: '',
        updated_by: '',
      },
    ]);
  };

  const isSaving = (row: EditableRow) => savingId === (row.id ?? 'new');

  const formattedRows = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        updated_at_display: r.updated_at ? new Date(r.updated_at).toLocaleString('en-GB') : '-',
        updated_by_display: r.updated_by || '-',
      })),
    [rows]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (accessDenied) {
    return (
      <Alert severity="error">
        Access denied. You do not have permission to manage LLM models.
      </Alert>
    );
  }

  return (
    <Paper elevation={1} sx={{ px: 3, pb: 3, pt: '10px', mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography
            className="text-general-page-header-prefix"
            sx={{ paddingBottom: '0 !important', mb: '0 !important' }}
          >
            LLM Models
          </Typography>
          <Typography
            className="text-general-page-header"
            sx={{ fontSize: '1.05rem', mb: '0 !important' }}
          >
            Manage provider/model selections
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <CustomButton text="Refresh" handleButtonPress={fetchModels} isButtonDisabled={loading} solid={false} />
          <CustomButton text="Add Row" handleButtonPress={handleAdd} isButtonDisabled={loading} solid />
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Model Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Updated At</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Updated By</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {formattedRows.map((row, idx) => (
            <TableRow key={row.id ?? `new-${idx}`}>
              <TableCell sx={{ minWidth: 140 }}>
                <Box sx={{ mt: '10px' }}>
                  <CustomTextInput
                    label="Provider"
                    value={row.provider}
                    onChange={(val) => typeof val === 'string' && handleChange(idx, 'provider', val)}
                    disabled={false}
                    margin="none"
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: 220 }}>
                <Box sx={{ mt: '10px' }}>
                  <CustomTextInput
                    label="Model Name"
                    value={row.model_name}
                    onChange={(val) => typeof val === 'string' && handleChange(idx, 'model_name', val)}
                    disabled={false}
                    margin="none"
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: 160, fontSize: '0.9rem', color: 'text.secondary' }}>
                {row.updated_at_display}
              </TableCell>
              <TableCell sx={{ minWidth: 140, fontSize: '0.9rem', color: 'text.secondary' }}>
                {row.updated_by_display}
              </TableCell>
              <TableCell align="right" sx={{ minWidth: 150 }}>
                <CustomButton
                  text={isSaving(row) ? 'Saving...' : 'Save'}
                  handleButtonPress={() => handleSave(idx)}
                  isButtonDisabled={isSaving(row)}
                  solid
                  widthOverride="100px"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Paper>
  );
};

